/**
 * API client service for making requests to the backend
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const TOKEN_KEY = 'annezedbee_token';

/**
 * Get stored auth token
 */
function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Store auth token
 */
function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }
}

/**
 * Clear auth token
 */
function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Get auth headers for fetch requests
 * Use this when you need to make authenticated fetch calls outside of apiRequest
 */
export function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Make an API request
 * @param {string} endpoint - API endpoint (e.g., '/auth/login')
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_URL}${endpoint}`;
  const token = getToken();

  const config = {
    ...options,
    credentials: 'include', // Include cookies (fallback)
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      // Clear token on auth errors
      if (response.status === 401) {
        clearToken();
      }
      throw new Error(data.error || 'Request failed');
    }

    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Authentication API
 */
export const auth = {
  async register(username, email, password) {
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    });
    // Store token from response
    if (response.token) {
      // setToken(response.token);
    }
    return response;
  },

  async login(email, password) {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    // Store token from response
    if (response.token) {
      setToken(response.token);
    }
    return response;
  },

  async logout() {
    const response = await apiRequest('/auth/logout', {
      method: 'POST',
    });
    // Clear stored token
    clearToken();
    return response;
  },

  async getCurrentUser() {
    return apiRequest('/auth/me');
  },

  // Utility to check if we have a stored token
  hasToken() {
    return !!getToken();
  },

  // Utility to clear token (for manual logout)
  clearToken() {
    clearToken();
  },
};

/**
 * Search API
 */
export const search = {
  async search(query, content_type = 'movie', category = null, pinVerified = false) {
    const body = { query, content_type };
    if (category) {
      body.category = category;
    }
    const headers = {};
    if (pinVerified) {
      headers['X-Pin-Verified'] = 'true';
    }
    return apiRequest('/search', {
      method: 'POST',
      body: JSON.stringify(body),
      headers,
    });
  },

  /**
   * Get available content categories
   * @returns {Promise<Object>} { success: boolean, categories: Array }
   */
  async getCategories() {
    return apiRequest('/search/categories');
  },
};

/**
 * Settings API
 */
export const settings = {
  async getIndexers() {
    return apiRequest('/settings/indexers');
  },

  async saveIndexer(indexer_name, api_key, is_active = true) {
    return apiRequest('/settings/indexers', {
      method: 'POST',
      body: JSON.stringify({ indexer_name, api_key, is_active }),
    });
  },

  async getSabNZBd() {
    return apiRequest('/settings/sabnzbd');
  },

  async saveSabNZBd(api_url, api_key, is_active = true) {
    return apiRequest('/settings/sabnzbd', {
      method: 'POST',
      body: JSON.stringify({ api_url, api_key, is_active }),
    });
  },

  async getPreferences() {
    return apiRequest('/settings/preferences');
  },

  async updatePreferences(adult_content_enabled) {
    return apiRequest('/settings/preferences', {
      method: 'PUT',
      body: JSON.stringify({ adult_content_enabled }),
    });
  },

  // PIN Management
  /**
   * Get PIN status (whether PIN is set, lockout status)
   * @returns {Promise<Object>} { hasPin: boolean, isLocked: boolean, lockoutRemainingSeconds: number }
   */
  async getPinStatus() {
    return apiRequest('/settings/pin');
  },

  /**
   * Set or update parental PIN
   * @param {string} pin - New PIN (4-6 digits)
   * @param {string} currentPin - Current PIN (required if updating existing)
   * @returns {Promise<Object>} { success: boolean, error?: string }
   */
  async setPin(pin, currentPin = null) {
    return apiRequest('/settings/pin', {
      method: 'POST',
      body: JSON.stringify({ pin, current_pin: currentPin }),
    });
  },

  /**
   * Remove parental PIN
   * @param {string} currentPin - Current PIN for verification
   * @returns {Promise<Object>} { success: boolean, error?: string }
   */
  async removePin(currentPin) {
    return apiRequest('/settings/pin', {
      method: 'DELETE',
      body: JSON.stringify({ current_pin: currentPin }),
    });
  },

  /**
   * Verify PIN for XXX category access
   * @param {string} pin - PIN to verify
   * @returns {Promise<Object>} { success: boolean, verified: boolean, attemptsRemaining?: number, error?: string }
   */
  async verifyPin(pin) {
    return apiRequest('/settings/pin/verify', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
  },

  // OpenSubtitles Configuration
  /**
   * Get OpenSubtitles configuration
   * @returns {Promise<Object>} { hasApiKey: boolean, primaryLanguage: string, secondaryLanguage: string }
   */
  async getOpenSubtitles() {
    return apiRequest('/settings/opensubtitles');
  },

  /**
   * Save OpenSubtitles configuration
   * @param {string} apiKey - OpenSubtitles API key
   * @param {string} primaryLanguage - Primary subtitle language
   * @param {string} secondaryLanguage - Secondary subtitle language
   * @returns {Promise<Object>} { success: boolean, error?: string }
   */
  async saveOpenSubtitles(apiKey, primaryLanguage, secondaryLanguage) {
    return apiRequest('/settings/opensubtitles', {
      method: 'POST',
      body: JSON.stringify({
        api_key: apiKey,
        primary_language: primaryLanguage,
        secondary_language: secondaryLanguage,
      }),
    });
  },

  /**
   * Remove OpenSubtitles configuration
   * @returns {Promise<Object>} { success: boolean }
   */
  async removeOpenSubtitles() {
    return apiRequest('/settings/opensubtitles', {
      method: 'DELETE',
    });
  },

  // Filter Preferences
  /**
   * Get filter preferences
   * @returns {Promise<Object>} Filter preferences object
   */
  async getFilters() {
    return apiRequest('/settings/filters');
  },

  /**
   * Save filter preferences
   * @param {Object} filters - Filter preferences to save
   * @returns {Promise<Object>} { success: boolean }
   */
  async saveFilters(filters) {
    return apiRequest('/settings/filters', {
      method: 'POST',
      body: JSON.stringify(filters),
    });
  },

  /**
   * Reset filters to defaults
   * @returns {Promise<Object>} { success: boolean, preferences: Object }
   */
  async resetFilters() {
    return apiRequest('/settings/filters', {
      method: 'DELETE',
    });
  },
};
